import type { Message } from "@ag-ui/client";
import { isHitlTool } from "./hitlTools";
import type { ToolCallEntry, Turn } from "./useTurns";

// Un-resulted tool calls on settled (non-running, non-cancelled) assistant
// turns. The run ended without finishing its work — either a HITL pause or an
// interrupted run, which `Chat` resolves by reconciling against `/connect`.
export function danglingToolCalls(turns: readonly Turn[]): ToolCallEntry[] {
  const calls: ToolCallEntry[] = [];
  for (const turn of turns) {
    if (turn.kind !== "assistant" || turn.isRunning || turn.cancelled) continue;
    calls.push(...turn.toolCalls.filter((tc) => tc.result === undefined));
  }
  return calls;
}

// Dangling calls that are genuine HITL pauses: only the hardcoded HITL tools
// (see hitlTools.ts) can pause a run for input, so a dangling call from any
// other tool never blocks the composer or renders an answer card.
export function hitlPauseIds(calls: readonly ToolCallEntry[]): ReadonlySet<string> {
  return new Set(calls.filter((tc) => isHitlTool(tc.name)).map((tc) => tc.id));
}

// Every HITL tool-call id in the raw AG-UI message list, answered or not. The
// message level is the only place a client-staged answer is visible before React
// recomputes the derived turns, and it is also what the wire carries — so both
// the resume gate below and `newOutboundMessages` read pauses from here rather
// than each deciding for itself which calls are HITL.
export function hitlCallIds(messages: readonly Message[]): Set<string> {
  const ids = new Set<string>();
  for (const message of messages) {
    if (message.role !== "assistant") continue;
    for (const call of message.toolCalls ?? []) {
      if (isHitlTool(call.function.name)) ids.add(call.id);
    }
  }
  return ids;
}

// HITL tool-call ids with no matching tool result yet. Read synchronously right
// after staging an answer to decide whether a deferred run may resume.
//
// The model can emit several HITL calls in one response (e.g. two
// `confirm_ingest` for two files), and pydantic-ai executes that set as a single
// step, so the server refuses a resume that leaves a sibling unanswered. This
// mirrors danglingToolCalls + hitlPauseIds at the message level to assemble the
// whole batch first: without it the card answered first would resume, take the
// refusal, and roll back, leaving a multi-card pause with no way through.
export function unansweredHitlCallIds(messages: readonly Message[]): Set<string> {
  const resulted = new Set<string>();
  for (const message of messages) {
    if (message.role === "tool") resulted.add(message.toolCallId);
  }
  return new Set([...hitlCallIds(messages)].filter((id) => !resulted.has(id)));
}
